<script setup>
/**
 * FormControlEmailMessageEvent
 * Displays a table of tracking events (delivered, opened, etc.)
 * for a specific email message.
 */
import {ref, watch, onMounted} from 'vue'
import moment from 'moment'
import {useEmailEventsStore} from '@/stores/emailEvents'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array, Object],
        default: null
    }
})

const emailEventsStore = useEmailEventsStore()

const items = ref([])
const isLoading = ref(false)

onMounted(() => {
    fetchEvents()
})

watch(() => props.currentData.id, () => {
    fetchEvents()
})

/**
 * Fetches the event history for the email message.
 */
const fetchEvents = async () => {
    if (!props.currentData.id) return

    isLoading.value = true
    try {
        const data = await emailEventsStore.get(props.currentData.id)
        items.value = data || []
    } catch (error) {
        console.error('Failed to fetch email events:', error)
        items.value = []
    } finally {
        isLoading.value = false
    }
}

/**
 * Maps event types to Bootstrap badge classes.
 */
const getClass = (event) => {
    const labels = {
        accepted: 'bg-warning text-dark',
        rejected: 'bg-danger',
        delivered: 'bg-success',
        failed: 'bg-danger',
        opened: 'bg-success',
        clicked: 'bg-success',
        unsubscribed: 'bg-danger',
        complained: 'bg-danger',
        stored: 'bg-warning text-dark'
    }

    return labels[event] || 'bg-secondary'
}

/**
 * Formats Unix timestamps for display.
 */
const getDate = (timestamp) => {
    return moment(timestamp).format('DD MMM YYYY, HH:mm')
}
</script>

<template>
    <div class="table-responsive">
        <table v-if="items.length" class="table">
            <thead>
            <tr>
                <th>Event</th>
                <th>Recipient</th>
                <th>Date</th>
                <th>Message</th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="item in items" :key="item.id">
                <td>
                        <span
                            :class="getClass(item.event)"
                            class="badge"
                            style="text-transform: capitalize"
                        >
                            {{ item.event }}
                        </span>
                </td>

                <td>{{ item.recipient }}</td>

                <td>{{ getDate(item.eventDate * 1000) }}</td>

                <td>{{ item.eventData?.deliveryStatus?.message }}</td>
            </tr>
            </tbody>
        </table>

        <div v-else-if="!isLoading" class="text-muted small p-2">
            No events recorded for this message.
        </div>
    </div>
</template>

<style scoped>
/* Optional styling to ensure the table doesn't overflow container */
.badge {
    font-weight: 500;
}
</style>